import { map, Observable, of } from 'rxjs';
import {
  CustomFieldProps,
  DynamicField,
  DynamicForm,
  EditorFieldProps,
  FieldVariant,
  SelectFieldProps,
  SelectOption,
  SwitchFieldProps,
  TextFieldProps,
} from '../../../../components/form-builder/form-builder.types';
import { AoRepository } from '@/store/ao-state/ao-state.repository';
import { ToggleSwitchChangeEvent } from 'primeng/toggleswitch';
import { PpmSlotsFormComponent } from '@/pages/ppm/milestone-form/slots-form/slots-form.component';
import { FundingDto, SlotDto } from '@/types';
import { PpmFundingSourceFormComponent } from '@/pages/ppm/milestone-form/funding-source-form/funding-source-form.component';

interface getCreateAoFormObjectProps {
  store: AoRepository;
  ppmOptions: Observable<SelectOption[]>;
  milestoneOptions: Observable<SelectOption[]>;
  hasSlots: Observable<boolean>;
  hasFundingSources: Observable<boolean>;
  currencyOptions?: SelectOption[];
  financialSourcesOptions?: (SelectOption & { examenPrealable: boolean })[];
}

export const getCreateAoFormObject = ({
  store,
  ppmOptions,
  milestoneOptions,
  hasSlots,
  hasFundingSources,
  currencyOptions,
  financialSourcesOptions,
}: getCreateAoFormObjectProps): DynamicForm => {
  const ppmField: DynamicField<SelectFieldProps> = {
    label: 'ao.form.ppm',
    variant: FieldVariant.SELECT,
    isRequired: true,
    error: store.getNestedObservable<string>('errors', 'ppmId'),
    props: {
      options: ppmOptions,
      value: store.getNestedObservable('ppmSelected'),
      onSelectChange: (value: SelectOption) => {
        store.set('ppmSelected', value);
        const code = value?.code as string;
        const [ppmId, ppmVersion] = code.split('-').map(Number);
        store.setNested('createDto', 'ppmId', ppmId);
        store.setNested('createDto', 'ppmVer', ppmVersion);
        store.clearError('ppmId');
      },
      placeholder: 'ao.form.placeholders.ppm',
    },
  };

  const milestoneField: DynamicField<SelectFieldProps> = {
    label: 'ao.form.milestone',
    variant: FieldVariant.SELECT,
    isRequired: true,
    isDisabled: store.getNestedObservable<boolean>('createDto', 'ppmId').pipe(map(ppmId => !ppmId)),
    error: store.getNestedObservable<string>('errors', 'realisationId'),
    props: {
      options: milestoneOptions,
      value: store.getNestedObservable('milestoneSelected'),
      onSelectChange: (value: SelectOption) => {
        store.set('milestoneSelected', value);
        const code = value?.code as string;
        const [milestoneId, milestoneVersion] = code.split('-').map(Number);
        store.setNested('createDto', 'realisationId', milestoneId);
        store.setNested('createDto', 'realisationVer', milestoneVersion);
        store.clearError('realisationId');
      },
      placeholder: 'ao.form.placeholders.milestone',
    },
  };

  const refField: DynamicField<TextFieldProps> = {
    label: 'ao.form.ref',
    variant: FieldVariant.TEXT,
    isRequired: true,
    isDisabled: of(true),
    error: store.getNestedObservable<string>('errors', 'reference'),
    props: {
      value: store.getNestedObservable<string>('createDto', 'reference'),
      placeholder: 'ao.form.placeholders.ref',
    },
  };

  const nameField: DynamicField<TextFieldProps> = {
    label: 'ao.form.name',
    variant: FieldVariant.TEXT,
    isRequired: true,
    error: store.getNestedObservable<string>('errors', 'nom'),
    props: {
      value: store.getNestedObservable<string>('createDto', 'nom'),
      onChange: (value: string) => {
        store.setNested('createDto', 'nom', value);
        store.clearError('nom');
      },
      placeholder: 'ao.form.placeholders.name',
    },
  };

  const descriptionField: DynamicField<EditorFieldProps> = {
    label: 'ao.form.description',
    variant: FieldVariant.EDITOR,
    isRequired: false,
    error: store.getNestedObservable<string>('errors', 'description'),
    props: {
      value: store.getNestedObservable<string>('createDto', 'description'),
      onChange: (value: string) => {
        store.setNested('createDto', 'description', value);
      },
      placeholder: 'ao.form.placeholders.description',
      height: '320px',
    },
  };

  //extra for display ********************************************************************************************
  const slotsField: DynamicField<CustomFieldProps> = {
    label: 'ao.form.slots',
    variant: FieldVariant.CUSTOM,
    isDisabled: of(true),
    isHidden: hasSlots.pipe(map((value: boolean) => !value)),
    props: {
      component: PpmSlotsFormComponent,
      value: store.getNestedObservable<SlotDto[]>('response.realisation.lots'),
      disabled: of(true),
      injectKeys: ['value', 'disabled'],
    },
  };

  const fundingSourcesField: DynamicField<CustomFieldProps> = {
    label: 'ao.form.funding',
    variant: FieldVariant.CUSTOM,
    isDisabled: of(true),
    isHidden: hasFundingSources.pipe(map((value: boolean) => !value)),
    props: {
      component: PpmFundingSourceFormComponent,
      disabled: of(true),
      currencyOptions: currencyOptions,
      financialSourcesOptions: financialSourcesOptions,
      value: store.getNestedObservable<FundingDto[]>('response.realisation.financements'),
      injectKeys: ['value', 'disabled', 'currencyOptions', 'financialSourcesOptions'],
    },
  };

  const acField: DynamicField<TextFieldProps> = {
    label: 'ao.form.ac',
    variant: FieldVariant.TEXT,
    isRequired: true,
    isDisabled: of(true),
    isHidden: store.getNestedObservable<boolean>('response', 'ppm').pipe(map(r => !r)),
    props: {
      value: store.getNestedObservable('response', 'ppm.ac.nom'),
    },
  };

  const passingModeField: DynamicField<TextFieldProps> = {
    label: 'ao.form.passingMode',
    variant: FieldVariant.TEXT,
    isRequired: true,
    isDisabled: of(true),
    isHidden: store.getNestedObservable<boolean>('response', 'realisation').pipe(map(r => !r)),
    props: {
      value: store.getNestedObservable('response', 'realisation.modePassation.libelle'),
    },
  };

  const passingModeTypeField: DynamicField<TextFieldProps> = {
    label: 'ao.form.portee',
    variant: FieldVariant.TEXT,
    isRequired: true,
    isDisabled: of(true),
    isHidden: store.getNestedObservable<boolean>('response', 'realisation').pipe(map(r => !r)),
    props: {
      value: store.getNestedObservable('response', 'realisation.modePassation.type'),
    },
  };

  const marketTypeField: DynamicField<TextFieldProps> = {
    label: 'ao.form.marketType',
    variant: FieldVariant.TEXT,
    isRequired: true,
    isDisabled: of(true),
    isHidden: store.getNestedObservable<boolean>('response', 'realisation').pipe(map(r => !r)),
    props: {
      value: store.getNestedObservable('response', 'realisation.typeMarcher.description'),
    },
  };

  return {
    title: 'ao.singular',
    description: 'ao.singular',
    orientation: 'horizontal',
    isHeaderHidden: true,
    grids: [
      {
        title: '',
        isHeaderHidden: true,
        gridItems: [
          {
            fields: [ppmField, milestoneField],
          },
          { fields: [acField, passingModeField] },
          {
            fields: [passingModeTypeField, marketTypeField],
          },
          {
            fields: [slotsField],
          },
          {
            fields: [fundingSourcesField],
          },
          { fields: [refField, nameField] },
          { fields: [descriptionField] },
        ],
      },
    ],
  };
};
